import { useChatContext } from "@/contexts/chatContext";
import { flattenMarkdown } from "@repo/components/src/shared/markdown/utils";
import {
  Avatar,
  AvatarFallbackText,
  AvatarImage,
} from "@repo/components/src/ui/avatar";
import { Box } from "@repo/components/src/ui/box";
import { Heading } from "@repo/components/src/ui/heading";
import { HStack } from "@repo/components/src/ui/hstack";
import { Icon } from "@repo/components/src/ui/icon";
import { Pressable } from "@repo/components/src/ui/pressable";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { MontuChatChatTypeChoices } from "@repo/graphql/gql/graphql";
import { Pin } from "lucide-react-native";
import React, { useMemo } from "react";
import ChatSelectorButtonActions from "./actions";
import { ChatsQuery } from "@repo/graphql/documents/chat/query.generated";

type Props = {
  edge: ChatsQuery["chat"]["chats"]["edges"][number];
};

const ChatSelectorButton = React.memo((props: Props) => {
  const { edge } = props;
  const { selectedChatId, setSelectedChatId } = useChatContext();
  const chat = edge.node;
  const user = edge.node.chatparticipantSet[0]?.user;
  const brand = edge.node.brand;
  const isGroup = chat.chatType.enum === MontuChatChatTypeChoices.Group;
  const profilePicture = isGroup ? brand.image : user?.profilePicture;
  const chatName = isGroup ? chat.groupName : user?.fullName;
  const lastMessage = edge.node.lastMessage;
  const isSelected = selectedChatId === chat.id;
  const hasUnread = chat.unreadByBrand > 0;
  const createdAt = useMemo(() => {
    if (!lastMessage?.createdAt) return null;
    const date = new Date(lastMessage.createdAt);
    const now = new Date();
    const sameDay =
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate();
    if (sameDay)
      return new Intl.DateTimeFormat("es", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(date);
    const startOfWeek = (d: Date) => {
      const copy = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      copy.setDate(copy.getDate() - copy.getDay());
      return copy.getTime();
    };
    if (startOfWeek(date) === startOfWeek(now))
      return new Intl.DateTimeFormat("es", { weekday: "short" }).format(date);
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yy = String(date.getFullYear()).slice(-2);
    return `${dd}/${mm}/${yy}`;
  }, [lastMessage]);
  const flattenedLastMessage = useMemo(() => {
    return flattenMarkdown(lastMessage?.message);
  }, [lastMessage]);
  return (
    <Pressable
      key={`Pressable: ${chat.id}`}
      onPress={() => setSelectedChatId(chat.id)}
    >
      {(pressableProps) => {
        const { hovered } = pressableProps;
        const wrapperClass = isSelected
          ? "bg-brand-50 border-brand-100"
          : hovered
            ? "bg-brand-50/40 border-brand-100/40"
            : "border-transparent";
        return (
          <HStack
            space="sm"
            className={`relative items-center px-3 py-2.5 rounded-xl border transition-colors ${wrapperClass}`}
          >
            {isSelected && (
              <Box className="absolute left-0 top-2 bottom-2 w-0.5 rounded-r-full bg-brand-500" />
            )}
            <Box className="relative shrink-0">
              <Avatar size="md">
                <AvatarFallbackText>{chatName}</AvatarFallbackText>
                <AvatarImage source={{ uri: profilePicture?.url }} />
              </Avatar>
            </Box>
            <VStack className="flex-1 shrink min-w-0" space="xs">
              <HStack space="xs" className="items-center">
                <Heading
                  size="sm"
                  bold={hasUnread}
                  className="flex-1 shrink min-w-0"
                  isTruncated
                  numberOfLines={1}
                >
                  {chatName}
                </Heading>
                {chat.isPinnedByBrand && (
                  <Icon
                    as={Pin}
                    className="w-3 h-3 shrink-0 fill-current"
                    action="brand"
                  />
                )}
                <Text
                  size="2xs"
                  className={`tracking-wide shrink-0 ${
                    hasUnread ? "text-brand-600" : "text-typography-400"
                  }`}
                  bold={hasUnread}
                >
                  {createdAt}
                </Text>
                <ChatSelectorButtonActions
                  hovered={hovered}
                  chatId={chat.id}
                  isPinned={chat.isPinnedByBrand}
                />
              </HStack>
              <HStack space="sm" className="items-center">
                <Text
                  size="xs"
                  isTruncated
                  numberOfLines={1}
                  className={`flex-1 shrink min-w-0 ${
                    hasUnread
                      ? "text-typography-700 font-semibold"
                      : "text-typography-500"
                  }`}
                >
                  {flattenedLastMessage}
                </Text>
                {hasUnread && (
                  <Box className="shrink-0 min-w-5 h-5 px-1.5 rounded-full bg-brand-500 items-center justify-center shadow-[0_3px_8px_-2px_rgba(124,58,237,0.55)]">
                    <Text
                      size="2xs"
                      bold
                      className="text-background-0 tabular-nums leading-none"
                    >
                      {chat.unreadByBrand}
                    </Text>
                  </Box>
                )}
              </HStack>
            </VStack>
          </HStack>
        );
      }}
    </Pressable>
  );
});

ChatSelectorButton.displayName = "ChatSelectorButton";

export default ChatSelectorButton;
